import React, { useEffect, useRef, useState } from 'react'

interface LazyImageProps {
  src: string
  alt?: string
  className?: string
  style?: React.CSSProperties
  webpSrc?: string
  srcSet?: string
  sizes?: string
  width?: number | string
  height?: number | string
}

const LazyImage: React.FC<LazyImageProps> = ({ src, alt = '', className = '', style, webpSrc, srcSet, sizes, width, height }) => {
  const imgRef = useRef<HTMLImageElement | null>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    const el = imgRef.current
    if (!el) return

    if ('loading' in HTMLImageElement.prototype) {
      // Browser supports native lazy loading; mark visible so we still handle blur-up
      setIsVisible(true)
      return
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      })
    }, { rootMargin: '200px' })

    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <picture className={`lazy-image ${className}`}>
      {webpSrc && isVisible && (
        <source srcSet={webpSrc} type="image/webp" />
      )}
      {srcSet && isVisible && (
        <source srcSet={srcSet} />
      )}
      <img
        ref={imgRef}
        src={isVisible ? src : ''}
        alt={alt}
        loading="lazy"
        sizes={sizes}
        width={width}
        height={height}
        onLoad={() => setLoaded(true)}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          ...style,
          transition: `filter 300ms ease, transform 300ms ease${style?.transition ? `, ${style.transition}` : ''}`,
          filter: loaded ? (style?.filter ?? 'none') : 'blur(12px)',
          transform: loaded ? (style?.transform ?? 'scale(1)') : 'scale(1.02)'
        }}
      />
    </picture>
  )
}

export default LazyImage
